import { useContext } from "react";
import { Dataset, DatasetFilters, datasetsConstants } from "../../../api/Datasets";
import {
  GenericEntityTable,
  GenericEntityTableControlProps,
  GenericEntityTableProps,
} from "../common/GenericEntityTable";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { DownloadDatasetMulti } from "../../../Dataset/common/DownloadDataset/DownloadDataset";
import { usePostMutation } from "../../../api/BaseEntityApi";
import { customImportConstants, CustomImportResult } from "../../../api/CustomImports";
import { ResourceName } from "../../../main/Routing";
import { useUnpaginateEntities } from "../../../common/forms/MultiEditForms/common/MultiEditUtils";
import { useEntityApi } from "../../../api/useEntityApi";
import { Button } from "../../../common/buttons/Button/Button";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { DatasetsFilterForm } from "../../../Dataset/Table/DatasetsFilterBar";

export const DatasetsTable = () => {
  return (
    <GenericEntityTable<Dataset, DatasetFilters, DatasetsFilterForm>
      entityTypeId="datasets"
      componentProps={DefaultDatasetTableComponentProps}
    />
  );
};

// Examples
export const DefaultDatasetTableComponentProps: GenericEntityTableProps<Dataset, DatasetFilters> = {
  additionalControlsTableOnScreen: (props) => <DatasetDownloadWrapper {...props} />,
  additionalControlsTableDropdown: (props) => <DatasetTableControlsWrapper {...props} />,
};

const DatasetDownloadWrapper = ({ selection }: { selection: Set<number> }) => {
  const { api } = useContext(SessionContext);
  return (
    <div>
      <DownloadDatasetMulti
        api={api}
        disabled={selection.size === 0}
        filters={{ ids: Array.from(selection) }}
        title={`Download the selected ${datasetsConstants.entityPlural}`}
      />
    </div>
  );
};

const DatasetTableControlsWrapper = ({
  selection,
  selectionPermissions,
  currentTab,
}: GenericEntityTableControlProps<Dataset, DatasetFilters>) => {
  const { session } = useContext(SessionContext);
  const { mutateAsync: bulkApplyCustomImportMutationAsync, isLoading: isLoadingBulkApplyCustomImportMutation } =
    usePostMutation<CustomImportResult[], { datasetIds: number[] }>({
      resource: "custom_imports/datasets" as ResourceName,
    });

  const { unpaginateEntities } = useUnpaginateEntities({ resource: datasetsConstants.resource });
  const { bulkEditMutationAsync, isLoadingBulkEditMutation } = useEntityApi<Dataset>(datasetsConstants.resource);
  return (
    <>
      {currentTab !== "unclaimed" && (
        <Button
          className="btn btn-ghost-danger"
          title={`${
            !selectionPermissions?.canEdit
              ? `Insufficient permissions to unclaim selected ${datasetsConstants.entityPlural}`
              : `Unclaim the selected ${datasetsConstants.entityPlural}`
          }`}
          disabled={selection.size === 0 || !selectionPermissions?.canEdit}
          onClick={async () => {
            const data = await unpaginateEntities<Dataset>(Array.from(selection));
            await bulkEditMutationAsync({
              body: Object.values(data).map((e) => ({ ...e, claimed: false })),
            }).catch(() => {});
          }}
          loading={isLoadingBulkEditMutation}
        >
          <LucideIcon name="circle-x" /> Unclaim
        </Button>
      )}
      {!!session?.permissions.logsadmin && (
        <Button
          className="btn btn-ghost-success"
          title="Bulk apply custom import"
          onClick={async () => {
            await bulkApplyCustomImportMutationAsync(
              { body: { datasetIds: Array.from(selection) } },
              {
                onSuccess: (data) => {
                  showtoast(
                    "success",
                    `Successfully applied custom imports to ${data.length ?? "N/A"}/${selection.size} datasets`
                  );
                  console.log("Bulk apply custom import result: ", data);
                },
              }
            ).catch(() => {});
          }}
          loading={isLoadingBulkApplyCustomImportMutation}
          disabled={selection.size === 0}
        >
          <LucideIcon name={customImportConstants.icon} /> Apply custom import
        </Button>
      )}
    </>
  );
};
